import React from 'react';

/**
 * Diyalog — SAP Fiori (Horizon) ortak UI primitive'leri.
 * Tüm sayfalar bu reçeteyle yeniden tasarlanır. İkonlar daima lucide-react (emoji/HTML simge YOK).
 * Marka mavisi sadece chrome/primary/seçim; statü = Horizon trafik-ışığı (success/warning/danger/info).
 */

// ── className reçeteleri (özel düzenler için doğrudan kullanılır) ──
export const fc = {
  card: 'bg-surface rounded-lg border border-hairline shadow-[0_1px_2px_rgba(15,26,58,0.04)]',
  btnPrimary: 'inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-brand-500/30',
  btnSecondary: 'inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium transition-colors disabled:opacity-50',
  btnGhost: 'inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md text-slate-600 hover:bg-slate-100 text-sm font-medium transition-colors',
  btnDanger: 'inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md bg-danger hover:bg-danger/90 text-white text-sm font-medium transition-colors disabled:opacity-50',
  input: 'h-9 px-3 rounded-md border border-slate-300 bg-white text-sm text-ink placeholder:text-slate-400 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition',
  select: 'h-9 px-3 rounded-md border border-slate-300 bg-white text-sm text-ink focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition',
  label: 'block text-sm font-medium text-slate-700 mb-1.5',
  table: 'w-full text-sm',
  thead: 'bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500 sticky top-0',
  th: 'text-left px-4 py-2.5 whitespace-nowrap',
  tr: 'border-b border-slate-100 hover:bg-slate-50 transition-colors',
  td: 'px-4 py-2.5 text-slate-700',
};

// ── Fiori object-page başlığı ──
export function PageHeader({ title, subtitle, icon: Icon, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
      <div className="flex items-center gap-3 min-w-0">
        {Icon && (
          <span className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-brand-600" />
          </span>
        )}
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-ink-strong tracking-tight truncate">{title}</h1>
          {subtitle && <p className="text-slate-500 text-sm mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

// ── Düz (flat) Fiori kart ──
export function Card({ className = '', children, ...rest }) {
  return <div className={`${fc.card} ${className}`} {...rest}>{children}</div>;
}

// ── Kart başlığı (ikon + başlık + sağ aksiyon) ──
export function CardHeader({ icon: Icon, tint = 'bg-brand-50', color = 'text-brand-600', title, sub, children }) {
  return (
    <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
      <div className="flex items-center gap-2.5 min-w-0">
        {Icon && (
          <span className={`w-7 h-7 rounded-md ${tint} flex items-center justify-center shrink-0`}>
            <Icon className={`w-4 h-4 ${color}`} />
          </span>
        )}
        <h2 className="text-[15px] font-semibold text-ink-strong truncate">{title}</h2>
        {sub && <span className="text-xs text-slate-400 hidden sm:inline">· {sub}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Horizon statü etiketi (markadan ayrık) ──
const TONE = {
  success: 'bg-success-bg text-success',
  warning: 'bg-warning-bg text-warning',
  danger: 'bg-danger-bg text-danger',
  info: 'bg-infoq-bg text-infoq',
  brand: 'bg-brand-50 text-brand-700',
  neutral: 'bg-slate-100 text-slate-600',
};
export function StatusPill({ tone = 'neutral', children, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${TONE[tone] || TONE.neutral} ${className}`}>
      {children}
    </span>
  );
}

// ── Buton (variant: primary | secondary | ghost | danger) ──
export function Btn({ variant = 'primary', className = '', ...props }) {
  const v = variant === 'secondary' ? fc.btnSecondary : variant === 'ghost' ? fc.btnGhost : variant === 'danger' ? fc.btnDanger : fc.btnPrimary;
  return <button className={`${v} ${className}`} {...props} />;
}

// ── Boş durum ──
export function EmptyState({ icon: Icon, title, hint, children }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6">
      {Icon && <span className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3"><Icon className="w-6 h-6 text-slate-400" /></span>}
      <p className="text-[15px] font-semibold text-ink-strong">{title}</p>
      {hint && <p className="text-sm text-slate-500 mt-1 max-w-sm">{hint}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}
